import React, { useRef, useEffect, useState, useCallback } from 'react';

const HEIGHT = 180;

// Draw-your-signature pad. Works with mouse, touch and pen through pointer events.
// Reports a transparent PNG data URL via onChange (null when empty or cleared).
function SignaturePad({ onChange, disabled }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [empty, setEmpty] = useState(true);

  // Size the backing store for the screen's pixel density so strokes stay crisp
  const setup = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = canvas.getBoundingClientRect().width || 600;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(HEIGHT * dpr);
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineWidth = 2.6;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
  }, []);

  useEffect(() => { setup(); }, [setup]);

  const point = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const down = (e) => {
    if (disabled) return;
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    const p = point(e);
    last.current = p;
    // A tap leaves a dot
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.arc(p.x, p.y, 1.3, 0, Math.PI * 2);
    ctx.fillStyle = '#111827';
    ctx.fill();
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = point(e);
    const mid = { x: (last.current.x + p.x) / 2, y: (last.current.y + p.y) / 2 };
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.quadraticCurveTo(last.current.x, last.current.y, mid.x, mid.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (empty) setEmpty(false);
  };

  const up = () => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    setEmpty(false);
    onChange?.(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    setup(); // resetting the size also wipes the canvas
    setEmpty(true);
    onChange?.(null);
  };

  return (
    <div className="sc-pad">
      <div className="sc-pad-surface">
        <canvas
          ref={canvasRef}
          className="sc-pad-canvas"
          style={{ height: HEIGHT }}
          aria-label="Draw your signature here"
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={up}
        />
        {empty && <span className="sc-pad-hint">Sign here with your finger or mouse</span>}
        <span className="sc-pad-line" />
      </div>
      <button type="button" className="sc-pad-clear" onClick={clear} disabled={empty || disabled}>Clear</button>
    </div>
  );
}

export default SignaturePad;
